import React, { useState, useEffect, useRef } from "react";
import { Folder, AlertTriangle } from "lucide-react";
import type { FolderPermission } from "@secure-caseworker/shared";
import { Modal } from "../../components/common/ui/modal";
import { FolderPermissionSelector } from "./FolderPermissionSelector";

export interface RenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  onRename: (newName: string) => Promise<void>;
  itemType: "file" | "folder";
}

export function RenameModal({
  isOpen,
  onClose,
  currentName,
  onRename,
  itemType,
}: RenameModalProps) {
  const [name, setName] = useState(currentName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setError(null);
      // Focus and select name without extension
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (itemType === "file") {
            const lastDotIndex = currentName.lastIndexOf(".");
            if (lastDotIndex > 0) {
              inputRef.current.setSelectionRange(0, lastDotIndex);
            } else {
              inputRef.current.select();
            }
          } else {
            inputRef.current.select();
          }
        }
      }, 100);
    }
  }, [isOpen, currentName, itemType]);

  const handleSubmit = async () => {
    setError(null);

    const trimmedName = name.trim();

    if (!trimmedName) {
      setError("Name cannot be empty");
      return;
    }

    if (trimmedName === currentName) {
      onClose();
      return;
    }

    if (/[<>:"/\\|?*]/.test(trimmedName)) {
      setError('Name contains invalid characters: < > : " / \\ | ? *');
      return;
    }

    setLoading(true);
    try {
      await onRename(trimmedName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Rename ${itemType}`}
      primaryAction={{
        label: loading ? "Renaming..." : "Rename",
        onClick: handleSubmit,
        disabled: loading,
        loading: loading,
      }}
      secondaryAction={{
        label: "Cancel",
        disabled: loading,
      }}
    >
      <form onSubmit={handleFormSubmit}>
        <div>
          <label
            htmlFor="rename-input"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            New name
          </label>
          <input
            ref={inputRef}
            id="rename-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none disabled:opacity-50"
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

export interface NewFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFolder: (
    name: string,
    clientPermission?: FolderPermission
  ) => Promise<void>;
  userRole?: string; // Organization role of current user (admin/staff/client)
}

export function NewFolderModal({
  isOpen,
  onClose,
  onCreateFolder,
  userRole,
}: NewFolderModalProps) {
  const [name, setName] = useState("");
  const [clientPermission, setClientPermission] =
    useState<FolderPermission>("none");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      // Default permission: 'modify' for clients, 'none' for admin/staff
      const defaultPermission = userRole === "client" ? "modify" : "none";
      setClientPermission(defaultPermission);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, userRole]);

  const handleSubmit = async () => {
    setError(null);

    const trimmedName = name.trim();

    if (!trimmedName) {
      setError("Folder name cannot be empty");
      return;
    }

    if (/[<>:"/\\|?*]/.test(trimmedName)) {
      setError('Name contains invalid characters: < > : " / \\ | ? *');
      return;
    }

    setLoading(true);
    try {
      await onCreateFolder(trimmedName, clientPermission);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create folder");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  const canSetPermissions = userRole === "admin" || userRole === "staff";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New Folder"
      primaryAction={{
        label: loading ? "Creating..." : "Create Folder",
        onClick: handleSubmit,
        disabled: loading,
        loading: loading,
      }}
      secondaryAction={{
        label: "Cancel",
        disabled: loading,
      }}
    >
      <form onSubmit={handleFormSubmit} className="space-y-4">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
              <Folder className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Create a new folder
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Organize your files
              </p>
            </div>
          </div>

          <label
            htmlFor="folder-name-input"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Folder name
          </label>
          <input
            ref={inputRef}
            id="folder-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            placeholder="My Folder"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none disabled:opacity-50"
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>

        {canSetPermissions && (
          <FolderPermissionSelector
            value={clientPermission}
            onChange={setClientPermission}
            disabled={loading}
          />
        )}
      </form>
    </Modal>
  );
}

export interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => Promise<void>;
  itemCount: number;
  itemNames?: string[];
}

export function DeleteConfirmModal({
  isOpen,
  onClose,
  onDelete,
  itemCount,
  itemNames = [],
}: DeleteConfirmModalProps) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Items"
      primaryAction={{
        label: loading ? "Deleting..." : "Delete",
        onClick: handleDelete,
        disabled: loading,
        loading: loading,
        variant: "danger",
      }}
      secondaryAction={{
        label: "Cancel",
        disabled: loading,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-950 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            Delete {itemCount} {itemCount === 1 ? "item" : "items"}?
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This action cannot be undone.
          </p>

          {itemNames.length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 max-h-32 overflow-y-auto">
              <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                {itemNames.slice(0, 5).map((name, i) => (
                  <li key={i} className="truncate">
                    • {name}
                  </li>
                ))}
                {itemNames.length > 5 && (
                  <li className="text-gray-500 dark:text-gray-500">
                    ...and {itemNames.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export interface ChangeFolderPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderName: string;
  currentPermission: FolderPermission;
  onChangePermission: (newPermission: FolderPermission) => Promise<void>;
}

export function ChangeFolderPermissionModal({
  isOpen,
  onClose,
  folderName,
  currentPermission,
  onChangePermission,
}: ChangeFolderPermissionModalProps) {
  const [permission, setPermission] =
    useState<FolderPermission>(currentPermission);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPermission(currentPermission);
      setError(null);
    }
  }, [isOpen, currentPermission]);

  const handleSubmit = async () => {
    setError(null);

    if (permission === currentPermission) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      await onChangePermission(permission);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to change permission"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Folder Permission"
      primaryAction={{
        label: loading ? "Updating..." : "Update Permission",
        onClick: handleSubmit,
        disabled: loading,
        loading: loading,
      }}
      secondaryAction={{
        label: "Cancel",
        disabled: loading,
      }}
    >
      <form onSubmit={handleFormSubmit} className="space-y-4">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
              <Folder className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {folderName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Set client access level
              </p>
            </div>
          </div>

          <FolderPermissionSelector
            value={permission}
            onChange={setPermission}
            disabled={loading}
          />

          {error && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}
